/**
 * Landing — IARisk eldritch hero.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  CodeRain (cyan glyphs, full-bleed)                     │
 *   │                                                         │
 *   │      [RUNE_ORBIT outer]                                 │
 *   │         [GAZE_EYE — follows cursor, lightning]          │
 *   │      [RUNE_ORBIT inner]                                 │
 *   │                                                         │
 *   │      IARisk  (glitch wordmark, RGB-split)              │
 *   │      EL OJO QUE TODO LO VE                              │
 *   │      [Entrar] [Crear cuenta] [Telegram widget]          │
 *   └─────────────────────────────────────────────────────────┘
 *
 *   ────── below the fold ──────
 *   FEATURES grid (cyber cards)
 *   FOOTER
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import webApi from "@/lib/webApi";
import GazeEye from "@/components/effects/GazeEye";
import CodeRain from "@/components/effects/CodeRain";
import RuneOrbit from "@/components/effects/RuneOrbit";
import GlitchText from "@/components/effects/GlitchText";
import {
  Brain, Eye, Sparkles, ShieldCheck, Mic, Terminal, Search,
  Coins, Send,
} from "lucide-react";

const FEATURES = [
  {
    icon: Brain,
    title: "Inteligencia sin filtros",
    desc: "Claude Sonnet 4.5 sin restricciones. Te responde lo que otras IAs no.",
    color: "rune",
  },
  {
    icon: Eye,
    title: "Visión & OCR",
    desc: "Le mandas captura, le mandas foto. Te dice qué dice, qué pasó, qué hacer.",
    color: "cyan",
  },
  {
    icon: Sparkles,
    title: "Generación de imágenes",
    desc: "Gemini Nano Banana. Pides, sale en segundos.",
    color: "rune",
  },
  {
    icon: Mic,
    title: "Dictado en vivo",
    desc: "Hablas, transcribimos con Whisper. El bot te responde por texto.",
    color: "cyan",
  },
  {
    icon: Terminal,
    title: "17 herramientas OSINT",
    desc: "Nmap · Shodan · WHOIS · Subdominios · IP Geoloc · Hash · Github Recon.",
    color: "rune",
  },
  {
    icon: ShieldCheck,
    title: "Anti-phishing",
    desc: "Cada link que pegues lo escaneo. URL maliciosa = aviso instantáneo.",
    color: "cyan",
  },
  {
    icon: Search,
    title: "Búsqueda web profunda",
    desc: "DuckDuckGo + Startpage sin huella. Yo navego por ti.",
    color: "rune",
  },
  {
    icon: Coins,
    title: "100 créditos GRATIS",
    desc: "Por registro completo. Te alcanzan para probarlo todo.",
    color: "cyan",
  },
];

export default function Landing() {
  const { user, loading } = useWebAuth();
  const navigate = useNavigate();
  const [botUsername, setBotUsername] = useState("IARisk_bot");

  useEffect(() => {
    webApi
      .get("/public/info")
      .then((r) => {
        if (r.data?.bot_username) setBotUsername(r.data.bot_username);
      })
      .catch(() => null);
  }, []);

  useEffect(() => {
    if (user && !loading) navigate("/chat", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div
      className="relative min-h-[100dvh] bg-obsidian text-zinc-100 overflow-x-hidden font-sans"
      data-testid="landing-page"
    >
      {/* GLOBAL BACKDROP — code rain + radial blood vignette (intensified) */}
      <div className="fixed inset-0 z-0 bg-obsidian">
        <CodeRain density={0.5} speed={0.6} fade={0.06} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 35%, rgba(255,26,58,0.42) 0%, rgba(255,26,58,0.20) 25%, transparent 55%), radial-gradient(ellipse at 50% 85%, rgba(0,224,255,0.16) 0%, transparent 55%)",
          }}
        />
      </div>

      {/* HEADER */}
      <header className="relative z-30 px-5 sm:px-8 lg:px-14 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-sm flex items-center justify-center border border-rune-red/40"
            style={{
              background:
                "radial-gradient(ellipse at 30% 30%, #ff1a3a 0%, #b8001f 45%, #220006 100%)",
              boxShadow:
                "0 0 18px rgba(255, 26, 58, 0.45), inset 0 0 10px rgba(0,0,0,0.5)",
            }}
          >
            <Eye size={16} className="text-white" strokeWidth={2.5} />
          </div>
          <div className="font-runic text-base tracking-[0.3em] text-white">
            IARISK
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <a
            href={`https://t.me/${botUsername}`}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] sm:text-xs font-mono tracking-widest text-data-cyan hover:text-white transition-colors flex items-center gap-1.5"
            data-testid="open-telegram-link"
          >
            <Send size={11} />
            ABRIR EN TELEGRAM
          </a>
          <Link
            to="/login"
            className="text-[10px] sm:text-xs font-mono tracking-widest text-zinc-400 hover:text-rune-red transition-colors"
            data-testid="header-login-link"
          >
            INICIAR SESIÓN →
          </Link>
        </div>
      </header>

      {/* HERO — THE EYE */}
      <section
        className="relative z-10 px-5 pt-6 pb-20 sm:pt-12 sm:pb-32 flex flex-col items-center text-center"
        data-testid="hero-section"
      >
        {/* Pre-title */}
        <div
          className="inline-flex items-center gap-2 px-3 py-1 border border-rune-red/40 text-[10px] font-mono tracking-[0.4em] text-rune-red mb-10 sm:mb-14"
          style={{ background: "rgba(40, 0, 8, 0.4)" }}
          data-testid="hero-eyebrow"
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-rune-red"
            style={{ boxShadow: "0 0 10px rgba(255,26,58,0.8)" }}
          />
          ACCESO SIN LÍMITES · TELEGRAM × WEB
        </div>

        {/* The Eye — center stage */}
        <div className="relative flex items-center justify-center w-[min(86vw,520px)] h-[min(86vw,520px)] mb-10 sm:mb-14">
          <RuneOrbit size={Math.min(620, typeof window !== "undefined" ? window.innerWidth * 0.92 : 620)} />
          <GazeEye size={Math.min(420, typeof window !== "undefined" ? window.innerWidth * 0.74 : 420)} />
        </div>

        {/* The wordmark */}
        <GlitchText
          size="text-6xl sm:text-7xl lg:text-8xl"
          tracking="tracking-[0.18em]"
          className="mb-3"
        >
          IARISK
        </GlitchText>

        {/* Slogan */}
        <p
          className="font-runic text-[11px] sm:text-sm tracking-[0.55em] text-rune-red mt-1 mb-3"
          style={{ textShadow: "0 0 14px rgba(255,26,58,0.55)" }}
          data-testid="hero-slogan"
        >
          EL OJO QUE TODO LO VE
        </p>
        <p
          className="font-glitch text-[11px] sm:text-xs text-data-cyan/80 mb-10 tracking-widest"
          data-testid="hero-tagline"
        >
          {">"} CONOCIMIENTO SIN REGLAS · ACCESO ILIMITADO
        </p>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch w-full max-w-xl"
          data-testid="hero-ctas"
        >
          <Link
            to="/registro"
            className="group relative flex-1 px-7 py-3.5 clip-rune font-runic tracking-[0.3em] text-sm text-white transition-all hover:scale-[1.02]"
            style={{
              background:
                "linear-gradient(135deg, #ff1a3a 0%, #b8001f 50%, #5a0008 100%)",
              boxShadow:
                "0 0 30px rgba(255,26,58,0.4), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -10px 24px rgba(90,0,8,0.5)",
            }}
            data-testid="hero-cta-register"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Eye size={14} strokeWidth={2.5} />
              ENTRAR EN EL OJO
            </span>
          </Link>
          <Link
            to="/login"
            className="flex-1 px-7 py-3.5 clip-rune font-runic tracking-[0.3em] text-sm text-data-cyan border border-data-cyan/40 hover:text-white hover:border-data-cyan transition-all"
            style={{
              background: "rgba(0, 30, 40, 0.6)",
              boxShadow: "inset 0 0 18px rgba(0, 224, 255, 0.15)",
            }}
            data-testid="hero-cta-login"
          >
            <span className="flex items-center justify-center gap-2">
              YA TENGO CUENTA
            </span>
          </Link>
        </div>

        {/* Optional: jump to the bot directly */}
        <a
          href={`https://t.me/${botUsername}`}
          target="_blank"
          rel="noreferrer"
          className="mt-6 text-[10px] font-mono tracking-[0.4em] text-zinc-500 hover:text-data-cyan transition-colors flex items-center gap-2"
          data-testid="hero-open-bot"
        >
          <Send size={11} />
          ¿PREFIERES EL BOT DE TELEGRAM? · ABRIR @{botUsername.toUpperCase()}
        </a>

        {/* Stats bar */}
        <div
          className="mt-12 grid grid-cols-3 gap-4 sm:gap-10 max-w-xl w-full text-center"
          data-testid="hero-stats"
        >
          {[
            { n: "100", l: "CRÉDITOS GRATIS" },
            { n: "17", l: "HERRAMIENTAS OSINT" },
            { n: "24/7", l: "SIN LÍMITES" },
          ].map((s) => (
            <div key={s.l}>
              <div
                className="font-runic text-2xl sm:text-3xl text-white"
                style={{
                  textShadow:
                    "0 0 18px rgba(255,26,58,0.5), 0 0 6px rgba(0,224,255,0.3)",
                }}
              >
                {s.n}
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES GRID */}
      <section
        className="relative z-10 px-5 sm:px-8 lg:px-14 py-20 max-w-6xl mx-auto"
        data-testid="features-section"
      >
        <div className="text-center mb-14">
          <div className="font-mono text-[10px] tracking-[0.5em] text-data-cyan mb-3">
            ── EL ARSENAL ──
          </div>
          <h2 className="font-runic text-3xl sm:text-4xl tracking-[0.2em] text-white mb-2">
            LO QUE EL OJO VE
          </h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            8 capacidades. Una sola cuenta. Sin descargar nada.
          </p>
        </div>

        <div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="features-grid"
        >
          {FEATURES.map((f, i) => {
            const I = f.icon;
            const isRune = f.color === "rune";
            return (
              <div
                key={f.title}
                className={`group relative p-5 clip-rune transition-all hover:translate-y-[-4px] ${
                  isRune
                    ? "border border-rune-red/30 hover:border-rune-red"
                    : "border border-data-cyan/30 hover:border-data-cyan"
                }`}
                style={{
                  background: isRune
                    ? "linear-gradient(180deg, rgba(40,0,10,0.5) 0%, rgba(8,2,5,0.85) 100%)"
                    : "linear-gradient(180deg, rgba(0,30,40,0.5) 0%, rgba(2,8,12,0.85) 100%)",
                  boxShadow: isRune
                    ? "inset 0 1px 0 rgba(255,80,120,0.15)"
                    : "inset 0 1px 0 rgba(0,224,255,0.18)",
                  animationDelay: `${i * 60}ms`,
                }}
                data-testid={`feature-card-${i}`}
              >
                <div
                  className={`w-10 h-10 mb-3 flex items-center justify-center rounded-sm ${
                    isRune
                      ? "bg-rune-red/15 text-rune-red"
                      : "bg-data-cyan/15 text-data-cyan"
                  }`}
                  style={{
                    boxShadow: isRune
                      ? "0 0 16px rgba(255,26,58,0.3)"
                      : "0 0 16px rgba(0,224,255,0.3)",
                  }}
                >
                  <I size={18} strokeWidth={2} />
                </div>
                <div className="font-runic text-[15px] tracking-[0.12em] text-white mb-1.5">
                  {f.title}
                </div>
                <div className="text-xs text-zinc-400 leading-relaxed">{f.desc}</div>
                <div
                  className={`absolute bottom-0 left-0 right-0 h-px ${
                    isRune ? "bg-rune-red" : "bg-data-cyan"
                  } opacity-40 group-hover:opacity-100 transition-opacity`}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA STRIP */}
      <section
        className="relative z-10 px-5 sm:px-8 lg:px-14 py-16"
        data-testid="final-cta"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono text-[10px] tracking-[0.5em] text-rune-red mb-3">
            ── LO ÚLTIMO ──
          </div>
          <h2 className="font-runic text-3xl sm:text-4xl tracking-[0.18em] text-white mb-4">
            ¿LISTO PARA QUE
            <br />
            EL OJO TE VEA?
          </h2>
          <p className="text-sm text-zinc-400 max-w-md mx-auto mb-8">
            100 créditos gratis al registrarte con perfil completo. 25 si solo
            usas email. Sin tarjeta. Sin truco.
          </p>
          <Link
            to="/registro"
            className="inline-flex items-center gap-2 px-10 py-4 clip-rune font-runic tracking-[0.3em] text-sm text-white transition-all hover:scale-105"
            style={{
              background:
                "linear-gradient(135deg, #ff1a3a 0%, #b8001f 50%, #5a0008 100%)",
              boxShadow:
                "0 0 40px rgba(255,26,58,0.5), inset 0 1px 0 rgba(255,255,255,0.20)",
            }}
            data-testid="final-cta-button"
          >
            <Eye size={14} strokeWidth={2.5} />
            ABRIR EL OJO
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-rune-red/15 px-5 sm:px-8 lg:px-14 py-6 text-center text-[10px] font-mono tracking-[0.4em] text-zinc-600">
        IARISK · TELEGRAM × WEB · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
